import axiosInstant from "../api";
import { signedDownload, zipFiles } from "../api/endpoints";
import {
  DownloadType,
  processSingleVideoDownload,
  ZipFileEntry,
} from "../pages/admin/videos/selectedVideoCardV2/helpers";

import {
  labelChannelInstances,
  UNASSIGNED_CHANNEL_NAME,
} from "../hooks/useChannelGroups";

import videoPlaceholder from "../assets/video_placeholder.png";

export const getVideoThumbnail = (video: any): string => {
  const url = video.location || video.reviewS3File?.location || '';
  if (url) return url.replace('.mp4', '_thumbnail.png');
  if (video.cloudFrontDomain && video.key) {
    return `${video.cloudFrontDomain}/${video.key}`.replace('.mp4', '_thumbnail.png');
  }
  return videoPlaceholder;
};

export const fileToIVideo = (file: any): any => ({
  ...file,
  id: file.id,
  location: file.location,
  key: file.key,
  title: file.review?.title || file.name,
  name: file.name,
  final: file.final ?? true,
  brandOwners: [],
  videoProducts: file.videoProducts,
  videoPros: file.videoPros,
  version: file.version,
  aspect_ratio: file.aspect_ratio,
  length: file.length,
  master: file.master,
});

export const buildZipSuffix = (type: DownloadType): string => {
  switch (type) {
    case DownloadType.HIGH_RES:
      return "-hd-original";
    case DownloadType.WEB:
      return "-hd-web-optimized";
    case DownloadType.WEBM:
      return "-hd-webm";
    default:
      return "";
  }
};

export const getWebmUrl = (location: string): string => {
  if (!location || typeof location !== "string") return "";
  try {
    const lastDot = location.lastIndexOf(".");
    if (lastDot <= 0) return "";
    return location.replace(location.substring(lastDot), "_optimized.webm");
  } catch {
    return "";
  }
};

export const isTimeoutError = (error: any): boolean =>
  error.code === "ECONNABORTED" ||
  error.code === "ERR_NETWORK" ||
  error.message?.toLowerCase().includes("timeout") ||
  error.response?.status === 503 ||
  error.response?.status === 504 ||
  error.response?.status === 408;

/**
 * Zip folder name for each of a project's files, matching the Downloads tab's
 * channel groups ("Sponsored", "Sponsored #2", ...). Returns undefined for
 * every file when the project has no channels, so legacy projects keep the
 * folders from their S3 keys.
 */
export const buildChannelFolderResolver = (
  channels: any[] | null | undefined,
): ((file: any) => string | undefined) => {
  const instances = labelChannelInstances(channels || []);
  if (instances.length === 0) return () => undefined;
  const byInstance: Record<string, string> = {};
  const firstByChannel: Record<string, string> = {};
  instances.forEach(({ channel, instanceId, name: label }) => {
    // Instance labels are free text; keep path separators out of the zip.
    const name = label.replace(/[\\/:*?"<>|]/g, "-");
    if (instanceId != null) byInstance[`${instanceId}`] = name;
    if (!firstByChannel[`${channel.id}`]) firstByChannel[`${channel.id}`] = name;
  });
  return (file: any) => {
    const instanceId = file.brand_project_channel_id;
    const channelId = file.content_channel_id;
    return (
      (instanceId != null && byInstance[`${instanceId}`]) ||
      (channelId != null && firstByChannel[`${channelId}`]) ||
      UNASSIGNED_CHANNEL_NAME
    );
  };
};

/**
 * Folder resolver across several projects (order-level zips): each file is
 * matched against its own project's channels. Files are keyed by id.
 */
export const buildProjectsFolderResolver = (
  projects: any[] | null | undefined,
): ((file: any) => string | undefined) => {
  const folderById = new Map<number, string | undefined>();
  (projects || []).forEach((project: any) => {
    const resolve = buildChannelFolderResolver(project.content_channels);
    (project.projectFiles || []).forEach((file: any) => {
      folderById.set(file.id, resolve(file));
    });
  });
  return (file: any) => folderById.get(file.id);
};

export const collectZipKeys = async (
  files: any[],
  type: DownloadType,
  allowedTypes: Set<string> | null = null,
  folderFor: (file: any) => string | undefined = () => undefined,
  // Staff and pre-approval brands get unapproved videos in the requested
  // format (as the per-video menu does); everyone else, their watermark.
  allowUnapproved = false,
): Promise<{ files: ZipFileEntry[]; hasContent: boolean }> => {
  const entries: ZipFileEntry[] = [];
  for (const file of files) {
    const video = fileToIVideo(file);
    const result = await processSingleVideoDownload(
      video,
      type,
      "temp.zip",
      true,
      video.final === false && !allowUnapproved,
      allowedTypes,
    );
    if (
      !result.error &&
      Array.isArray(result.files) &&
      result.files.length > 0
    ) {
      const folder = folderFor(file);
      result.files.forEach((entry) =>
        entries.push(folder ? { ...entry, folder } : entry),
      );
    }
  }
  return { files: entries, hasContent: entries.length > 0 };
};

/**
 * Resolve a clickable download link that forces a human-readable filename.
 * When a bucket+key are known, presign the object so it streams from S3 with
 * the name forced via Content-Disposition (the `<a download>` attribute is
 * ignored for cross-origin URLs). Falls back to the raw URL (no rename) when
 * there's no bucket/key or presigning fails — never throws.
 */
export const getRenamedDownload = async (opts: {
  bucket?: string;
  key?: string;
  fileName: string;
  fallbackUrl?: string;
}): Promise<{ link: string; fileName: string }> => {
  const { bucket, key, fileName, fallbackUrl } = opts;
  // Any key presigns (the API defaults the bucket for legacy files that have
  // none): the signed URL's Content-Disposition is what gives every download
  // the same friendly filename, on share pages and in the app.
  if (key) {
    try {
      const { data } = await axiosInstant.post(signedDownload(), {
        ...(bucket ? { bucket } : {}),
        key,
        fileName,
      });
      if (data?.url) return { link: data.url, fileName };
    } catch {
      // fall through to the direct link
    }
  }
  return { link: fallbackUrl || "", fileName };
};

/** Order / project the zip is for: the API adds the deliverables tracker. */
export type ZipTracker = { orderId: number } | { projectId: number };

export const submitZip = async (
  files: ZipFileEntry[],
  zipFileName: string,
  roomId: string,
  tracker?: ZipTracker,
): Promise<string | null> => {
  const zip = await axiosInstant.post(zipFiles(), {
    zipFileName,
    files,
    roomId,
    ...(tracker ? { tracker } : {}),
  });
  return zip.data?.fileName ?? null;
};
